import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS, type OptionReorderScope } from "../../../shared/constants/api";
import type {
  ProductConfigDto,
  CategoryDto,
  DeleteProductResult,
  ProductUsage,
  ProductLightDto,
  ProductPaperDto,
  GroupedProductPaperDto,
  AvailablePaperOptionsDto,
  SizeTemplateDto,
  ProductInkConfigDto,
  OptionDto,
  OptionAttributeDto,
  OptionAttributeValueDto,
  MaterialDto,
  MaterialSheetPaperDto,
  MaterialRollPaperDto,
  InkDto,
  InkConfigDto,
  OtherMaterialDto,
  PaperSpecDto,
  RollSizeSpecDto,
  SheetSizeSpecDto,
  SheetSizePresetType,
  PriceCalculationResult,
  CreateProductRequest,
  UpdateProductRequest,
  DuplicateProductRequest,
  CreateProductPaperRequest,
  CreateSizeTemplateRequest,
  CreateOptionRequest,
  CreateOptionAttributeRequest,
  CreateOptionAttributeValueRequest,
  PriceCalculationRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  MaterialInventoryDto,
  MaterialInventoryLogDto,
  InventoryRequest,
  InventoryLogPageResponse,
  EstimatePreviewResponse,
  EstimatePreviewRequest,
} from "../../../domain/types/company/company-product-system";

interface ApiResponse<T> {
  data: T | null;
  errorMsg: string | null;
  errorCode: number | null;
}

export class CompanyProductSystemApiService {
  // ========== Product Config ==========

  async getProductConfig(companyId: number): Promise<ApiResponse<ProductConfigDto>> {
    const response = await httpClient.get<ApiResponse<ProductConfigDto>>(
      NEW_API_ENDPOINTS.companyProductConfigController.GET(companyId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품 설정을 가져오지 못했습니다.");
    }
    return response.data;
  }

  // ========== Categories ==========

  async getCategories(companyId: number): Promise<ApiResponse<CategoryDto[]>> {
    const response = await httpClient.get<ApiResponse<CategoryDto[]>>(
      NEW_API_ENDPOINTS.companyCategoryController.GET(companyId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "카테고리 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createCategory(
    companyId: number,
    request: CreateCategoryRequest,
  ): Promise<ApiResponse<CategoryDto>> {
    const response = await httpClient.post<ApiResponse<CategoryDto>>(
      NEW_API_ENDPOINTS.companyCategoryController.POST(companyId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "카테고리를 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateCategory(
    companyId: number,
    categoryId: number,
    request: UpdateCategoryRequest,
  ): Promise<ApiResponse<CategoryDto>> {
    const response = await httpClient.put<ApiResponse<CategoryDto>>(
      NEW_API_ENDPOINTS.companyCategoryController.PUT(companyId, categoryId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "카테고리를 수정하지 못했습니다.");
    }
    return response.data;
  }

  async deleteCategory(companyId: number, categoryId: number): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyCategoryController.DELETE(companyId, categoryId),
    );
    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "카테고리를 삭제하지 못했습니다.");
    }
    return response.data;
  }

  async bulkUpdateCategoryOrder(
    companyId: number,
    orders: Array<{ categoryId: number; displayOrder: number }>,
  ): Promise<ApiResponse<CategoryDto[]>> {
    const response = await httpClient.patch<ApiResponse<CategoryDto[]>>(
      NEW_API_ENDPOINTS.companyCategoryController.BULK_UPDATE_ORDER(companyId),
      { orders },
    );
    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "카테고리 순서를 변경하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Products (plural) ==========

  async getProducts(companyId: number): Promise<ApiResponse<ProductLightDto[]>> {
    const response = await httpClient.get<ApiResponse<ProductLightDto[]>>(
      NEW_API_ENDPOINTS.companyProductsController.GET(companyId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async getProduct(companyId: number, productId: number): Promise<ApiResponse<ProductLightDto>> {
    const response = await httpClient.get<ApiResponse<ProductLightDto>>(
      NEW_API_ENDPOINTS.companyProductsController.GET_DETAIL(companyId, productId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품 상세 정보를 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createProduct(
    companyId: number,
    request: CreateProductRequest,
  ): Promise<ApiResponse<ProductLightDto>> {
    const response = await httpClient.post<ApiResponse<ProductLightDto>>(
      NEW_API_ENDPOINTS.companyProductsController.POST(companyId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품을 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateProduct(
    companyId: number,
    productId: number,
    request: UpdateProductRequest,
  ): Promise<ApiResponse<ProductLightDto>> {
    const response = await httpClient.put<ApiResponse<ProductLightDto>>(
      NEW_API_ENDPOINTS.companyProductsController.PUT(companyId, productId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품을 수정하지 못했습니다.");
    }
    return response.data;
  }

  async duplicateProduct(
    companyId: number,
    productId: number,
    request: DuplicateProductRequest,
  ): Promise<ApiResponse<ProductLightDto>> {
    const response = await httpClient.post<ApiResponse<ProductLightDto>>(
      NEW_API_ENDPOINTS.companyProductsController.DUPLICATE(companyId, productId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품을 복제하지 못했습니다.");
    }
    return response.data;
  }

  async uploadProductThumbnail(
    companyId: number,
    productId: number,
    file: File,
  ): Promise<ApiResponse<ProductLightDto>> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await httpClient.post<ApiResponse<ProductLightDto>>(
      `${NEW_API_ENDPOINTS.companyProductsController.GET_DETAIL(companyId, productId)}/thumbnail`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "썸네일을 업로드하지 못했습니다.");
    }
    return response.data;
  }

  /**
   * 상품 삭제.
   *
   * `confirmed` 없이 호출했는데 상품이 아직 사용 중이면 백엔드가 409 로 막고
   * `data.usages` 에 사용처 요약을 실어 보낸다. 그 409 만 예외에서 건져내
   * `{ deleted: false, usages }` 정상 결과로 바꾼다 — 삭제 실패가 아니라
   * 사용자 재확인을 기다리는 상태이기 때문이다. 나머지 에러는 그대로 던진다.
   *
   * @param confirmed 사용 중이어도 삭제할지 여부. 경고 모달에서 "삭제"를 누른 재요청에만 true
   */
  async deleteProduct(
    companyId: number,
    productId: number,
    confirmed = false,
  ): Promise<DeleteProductResult> {
    try {
      const response = await httpClient.delete<ApiResponse<boolean>>(
        NEW_API_ENDPOINTS.companyProductsController.DELETE(companyId, productId),
        { params: confirmed ? { confirmed: true } : undefined },
      );
      if (response.data.errorCode) {
        throw new Error(response.data.errorMsg || "상품을 삭제하지 못했습니다.");
      }
      return { deleted: true, usages: [] };
    } catch (error) {
      const usages = (error as { data?: { usages?: ProductUsage[] } })?.data?.usages;
      if ((error as { status?: number })?.status === 409 && usages?.length) {
        return {
          deleted: false,
          usages,
          message: (error as Error).message,
        };
      }
      throw error;
    }
  }

  async bulkUpdateProductOrder(
    companyId: number,
    orders: Array<{ productId: number; displayOrder: number }>,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.patch<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyProductsController.BULK_UPDATE_ORDER(companyId),
      { orders },
    );
    if (response.data?.errorCode) {
      throw new Error(response.data.errorMsg || "상품 순서를 변경하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Product Paper ==========

  async getProductPapers(
    companyId: number,
    productId: number,
  ): Promise<ApiResponse<ProductPaperDto[]>> {
    const response = await httpClient.get<ApiResponse<ProductPaperDto[]>>(
      NEW_API_ENDPOINTS.companyProductPaperController.GET(companyId, productId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품 용지 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createProductPaper(
    companyId: number,
    productId: number,
    request: CreateProductPaperRequest,
  ): Promise<ApiResponse<ProductPaperDto>> {
    const response = await httpClient.post<ApiResponse<ProductPaperDto>>(
      NEW_API_ENDPOINTS.companyProductPaperController.POST(companyId, productId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품 용지를 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateProductPaper(
    companyId: number,
    productId: number,
    paperId: number,
    request: Partial<CreateProductPaperRequest>,
  ): Promise<ApiResponse<ProductPaperDto>> {
    const response = await httpClient.put<ApiResponse<ProductPaperDto>>(
      NEW_API_ENDPOINTS.companyProductPaperController.PUT(companyId, productId, paperId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품 용지를 수정하지 못했습니다.");
    }
    return response.data;
  }

  async deleteProductPaper(
    companyId: number,
    productId: number,
    paperId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyProductPaperController.DELETE(companyId, productId, paperId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품 용지를 삭제하지 못했습니다.");
    }
    return response.data;
  }

  /**
   * 상품에 연결된 용지를 Material(용지 재질) 이름으로 그룹핑하여 조회합니다.
   */
  async getGroupedProductPapers(
    companyId: number,
    productId: number,
  ): Promise<ApiResponse<GroupedProductPaperDto[]>> {
    const response = await httpClient.get<ApiResponse<GroupedProductPaperDto[]>>(
      `${NEW_API_ENDPOINTS.companyProductPaperController.GET(companyId, productId)}/grouped`,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "그룹핑된 상품 용지 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  /**
   * 동적 용지 옵션 필터링 조회
   *
   * 견적식 미리보기에서 용지 종류/평량 선택 시 상호 필터링을 제공합니다.
   * - 평량 먼저 선택 → 해당 평량을 지원하는 용지 종류만 반환
   * - 용지 종류 먼저 선택 → 해당 용지에서 사용 가능한 평량만 반환
   * - 둘 다 선택 시 → 매칭되는 Material ID 반환
   */
  async getAvailablePaperOptions(
    companyId: number,
    productId: number,
    paperUsageType: string,
    selectedMaterialName?: string,
    selectedGrammage?: number,
  ): Promise<ApiResponse<AvailablePaperOptionsDto>> {
    const params = new URLSearchParams({ paperUsageType });
    if (selectedMaterialName) {
      params.append("selectedMaterialName", selectedMaterialName);
    }
    if (selectedGrammage !== undefined && selectedGrammage !== null) {
      params.append("selectedGrammage", selectedGrammage.toString());
    }

    const response = await httpClient.get<ApiResponse<AvailablePaperOptionsDto>>(
      `${NEW_API_ENDPOINTS.companyProductPaperController.GET(companyId, productId)}/available-options?${params.toString()}`,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "사용 가능한 용지 옵션을 가져오지 못했습니다.");
    }
    return response.data;
  }

  // ========== Size Template ==========

  async getSizeTemplates(
    companyId: number,
    productId: number,
  ): Promise<ApiResponse<SizeTemplateDto[]>> {
    const response = await httpClient.get<ApiResponse<SizeTemplateDto[]>>(
      NEW_API_ENDPOINTS.companySizeTemplateController.GET(companyId, productId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "사이즈 템플릿 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createSizeTemplate(
    companyId: number,
    productId: number,
    request: CreateSizeTemplateRequest,
  ): Promise<ApiResponse<SizeTemplateDto>> {
    const response = await httpClient.post<ApiResponse<SizeTemplateDto>>(
      NEW_API_ENDPOINTS.companySizeTemplateController.POST(companyId, productId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "사이즈 템플릿을 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateSizeTemplate(
    companyId: number,
    productId: number,
    sizeTemplateId: number,
    request: Partial<CreateSizeTemplateRequest>,
  ): Promise<ApiResponse<SizeTemplateDto>> {
    const response = await httpClient.put<ApiResponse<SizeTemplateDto>>(
      NEW_API_ENDPOINTS.companySizeTemplateController.PUT(companyId, productId, sizeTemplateId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "사이즈 템플릿을 수정하지 못했습니다.");
    }
    return response.data;
  }

  async deleteSizeTemplate(
    companyId: number,
    productId: number,
    sizeTemplateId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companySizeTemplateController.DELETE(companyId, productId, sizeTemplateId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "사이즈 템플릿을 삭제하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Product InkConfig ==========

  async getProductInkConfigs(
    companyId: number,
    productId: number,
  ): Promise<ApiResponse<ProductInkConfigDto[]>> {
    const response = await httpClient.get<ApiResponse<ProductInkConfigDto[]>>(
      NEW_API_ENDPOINTS.companyProductInkConfigController.GET(companyId, productId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품 인쇄 구성 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createProductInkConfig(
    companyId: number,
    productId: number,
    inkConfigId: number,
  ): Promise<ApiResponse<ProductInkConfigDto>> {
    const response = await httpClient.post<ApiResponse<ProductInkConfigDto>>(
      NEW_API_ENDPOINTS.companyProductInkConfigController.POST(companyId, productId),
      { inkConfigId },
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품 인쇄 구성을 추가하지 못했습니다.");
    }
    return response.data;
  }

  async deleteProductInkConfig(
    companyId: number,
    productId: number,
    productInkConfigId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyProductInkConfigController.DELETE(companyId, productId, productInkConfigId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "상품 인쇄 구성을 삭제하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Options (product-scoped) ==========

  async getOptions(
    companyId: number,
    productId: number,
  ): Promise<ApiResponse<OptionDto[]>> {
    const response = await httpClient.get<ApiResponse<OptionDto[]>>(
      NEW_API_ENDPOINTS.companyProductOptionController.GET(companyId, productId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async getOption(
    companyId: number,
    productId: number,
    optionId: number,
  ): Promise<ApiResponse<OptionDto>> {
    const response = await httpClient.get<ApiResponse<OptionDto>>(
      NEW_API_ENDPOINTS.companyProductOptionController.GET_DETAIL(companyId, productId, optionId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션 상세 정보를 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createOption(
    companyId: number,
    productId: number,
    request: CreateOptionRequest,
  ): Promise<ApiResponse<OptionDto>> {
    const response = await httpClient.post<ApiResponse<OptionDto>>(
      NEW_API_ENDPOINTS.companyProductOptionController.POST(companyId, productId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션을 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateOption(
    companyId: number,
    productId: number,
    optionId: number,
    request: Partial<CreateOptionRequest>,
  ): Promise<ApiResponse<OptionDto>> {
    const response = await httpClient.put<ApiResponse<OptionDto>>(
      NEW_API_ENDPOINTS.companyProductOptionController.PUT(companyId, productId, optionId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션을 수정하지 못했습니다.");
    }
    return response.data;
  }

  async reorderOptionItems(
    companyId: number,
    productId: number,
    scope: OptionReorderScope,
    orderedIds: number[],
  ): Promise<void> {
    const response = await httpClient.put<ApiResponse<unknown>>(
      NEW_API_ENDPOINTS.companyProductOptionController.PUT_ORDER(companyId, productId, scope),
      { orderedIds },
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "순서를 저장하지 못했습니다.");
    }
  }

  async deleteOption(
    companyId: number,
    productId: number,
    optionId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyProductOptionController.DELETE(companyId, productId, optionId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션을 삭제하지 못했습니다.");
    }
    return response.data;
  }

  async addInsertPaper(
    companyId: number,
    productId: number,
  ): Promise<ApiResponse<OptionDto>> {
    const response = await httpClient.post<ApiResponse<OptionDto>>(
      NEW_API_ENDPOINTS.companyProductOptionController.POST_INSERT_PAPER(companyId, productId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "끼움지를 추가하지 못했습니다.");
    }
    return response.data;
  }

  async addEndPaper(
    companyId: number,
    productId: number,
  ): Promise<ApiResponse<OptionDto>> {
    const response = await httpClient.post<ApiResponse<OptionDto>>(
      NEW_API_ENDPOINTS.companyProductOptionController.POST_END_PAPER(companyId, productId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "면지를 추가하지 못했습니다.");
    }
    return response.data;
  }

  async addPaperOrientation(
    companyId: number,
    productId: number,
  ): Promise<ApiResponse<OptionDto>> {
    const response = await httpClient.post<ApiResponse<OptionDto>>(
      NEW_API_ENDPOINTS.companyProductOptionController.POST_PAPER_ORIENTATION(companyId, productId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "용지 방향을 추가하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Option Attributes ==========

  async createOptionAttribute(
    companyId: number,
    productId: number,
    optionId: number,
    request: CreateOptionAttributeRequest,
  ): Promise<ApiResponse<OptionAttributeDto>> {
    const response = await httpClient.post<ApiResponse<OptionAttributeDto>>(
      NEW_API_ENDPOINTS.companyProductOptionAttributeController.POST(companyId, productId, optionId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션 속성을 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateOptionAttribute(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
    request: Partial<CreateOptionAttributeRequest>,
  ): Promise<ApiResponse<OptionAttributeDto>> {
    const response = await httpClient.put<ApiResponse<OptionAttributeDto>>(
      NEW_API_ENDPOINTS.companyProductOptionAttributeController.PUT(companyId, productId, optionId, attributeId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션 속성을 수정하지 못했습니다.");
    }
    return response.data;
  }

  async deleteOptionAttribute(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyProductOptionAttributeController.DELETE(companyId, productId, optionId, attributeId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션 속성을 삭제하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Option Attribute Values ==========

  async createOptionAttributeValue(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
    request: CreateOptionAttributeValueRequest,
    imageFile?: File,
  ): Promise<ApiResponse<OptionAttributeValueDto>> {
    const payload = imageFile
      ? (() => {
          const formData = new FormData();
          formData.append("request", new Blob([JSON.stringify(request)], { type: "application/json" }));
          formData.append("imageFile", imageFile);
          return formData;
        })()
      : request;
    const response = await httpClient.post<ApiResponse<OptionAttributeValueDto>>(
      NEW_API_ENDPOINTS.companyProductOptionAttributeValueController.POST(companyId, productId, optionId, attributeId),
      payload,
      imageFile ? { headers: { "Content-Type": "multipart/form-data" } } : undefined,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션 값을 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateOptionAttributeValue(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
    valueId: number,
    request: Partial<CreateOptionAttributeValueRequest>,
    imageFile?: File,
  ): Promise<ApiResponse<OptionAttributeValueDto>> {
    const payload = imageFile
      ? (() => {
          const formData = new FormData();
          formData.append("request", new Blob([JSON.stringify(request)], { type: "application/json" }));
          formData.append("imageFile", imageFile);
          return formData;
        })()
      : request;
    const response = await httpClient.put<ApiResponse<OptionAttributeValueDto>>(
      NEW_API_ENDPOINTS.companyProductOptionAttributeValueController.PUT(companyId, productId, optionId, attributeId, valueId),
      payload,
      imageFile ? { headers: { "Content-Type": "multipart/form-data" } } : undefined,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션 값을 수정하지 못했습니다.");
    }
    return response.data;
  }

  async deleteOptionAttributeValue(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
    valueId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyProductOptionAttributeValueController.DELETE(companyId, productId, optionId, attributeId, valueId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "옵션 값을 삭제하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Price Calculation ==========

  async calculatePrice(
    companyId: number,
    productId: number,
    request: PriceCalculationRequest,
  ): Promise<ApiResponse<PriceCalculationResult>> {
    const response = await httpClient.post<ApiResponse<PriceCalculationResult>>(
      NEW_API_ENDPOINTS.companyPriceCalculationController.POST(companyId, productId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "가격 계산에 실패했습니다.");
    }
    return response.data;
  }

  // ========== Estimate Preview ==========

  /**
   * 견적식 미리보기 조회
   *
   * 상품의 사이즈 템플릿과 용지 조합에 대해
   * 각 조합별 절지 규격과 가격을 표 형태로 제공합니다.
   *
   * @param companyId 회사 ID
   * @param productId 상품 ID
   * @param quantity 수량 (기본값: 1)
   */
  async getEstimatePreview(
    companyId: number,
    productId: number,
    quantity: number = 1,
  ): Promise<ApiResponse<EstimatePreviewResponse>> {
    const request: EstimatePreviewRequest = { quantity };
    const response = await httpClient.post<ApiResponse<EstimatePreviewResponse>>(
      NEW_API_ENDPOINTS.companyEstimatePreviewController.POST(companyId, productId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "견적식 미리보기를 가져오지 못했습니다.");
    }
    return response.data;
  }

  // ========== Paper Spec (company level) ==========

  async getPaperSpecs(companyId: number, includeAutoGenerated?: boolean): Promise<ApiResponse<PaperSpecDto[]>> {
    const params: Record<string, any> = {};
    if (includeAutoGenerated) params.includeAutoGenerated = true;
    const response = await httpClient.get<ApiResponse<PaperSpecDto[]>>(
      NEW_API_ENDPOINTS.companyPaperSpecController.GET(companyId),
      { params },
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "종이 규격 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  // ========== Sheet Size Spec (company level, 회사가 직접 CRUD 관리) ==========

  async getSheetSizeSpecs(companyId: number): Promise<ApiResponse<SheetSizeSpecDto[]>> {
    const response = await httpClient.get<ApiResponse<SheetSizeSpecDto[]>>(
      NEW_API_ENDPOINTS.companySheetSizeSpecController.GET(companyId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "시트 사이즈 규격 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createSheetSizeSpec(
    companyId: number,
    request: { name?: string; width: number; height: number; presetType?: SheetSizePresetType },
  ): Promise<ApiResponse<SheetSizeSpecDto>> {
    const response = await httpClient.post<ApiResponse<SheetSizeSpecDto>>(
      NEW_API_ENDPOINTS.companySheetSizeSpecController.POST(companyId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "시트 사이즈 규격을 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateSheetSizeSpec(
    companyId: number,
    sheetSizeSpecId: number,
    request: { name?: string; width: number; height: number },
  ): Promise<ApiResponse<SheetSizeSpecDto>> {
    const response = await httpClient.put<ApiResponse<SheetSizeSpecDto>>(
      NEW_API_ENDPOINTS.companySheetSizeSpecController.PUT(companyId, sheetSizeSpecId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "시트 사이즈 규격을 수정하지 못했습니다.");
    }
    return response.data;
  }

  async deleteSheetSizeSpec(companyId: number, sheetSizeSpecId: number): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companySheetSizeSpecController.DELETE(companyId, sheetSizeSpecId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "시트 사이즈 규격을 삭제하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Roll Size Spec (company level, 회사가 직접 CRUD 관리) ==========

  async getRollSizeSpecs(companyId: number): Promise<ApiResponse<RollSizeSpecDto[]>> {
    const response = await httpClient.get<ApiResponse<RollSizeSpecDto[]>>(
      NEW_API_ENDPOINTS.companyRollSizeSpecController.GET(companyId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "롤 사이즈 규격 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createRollSizeSpec(
    companyId: number,
    request: { name?: string; width: number },
  ): Promise<ApiResponse<RollSizeSpecDto>> {
    const response = await httpClient.post<ApiResponse<RollSizeSpecDto>>(
      NEW_API_ENDPOINTS.companyRollSizeSpecController.POST(companyId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "롤 사이즈 규격을 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateRollSizeSpec(
    companyId: number,
    rollSizeSpecId: number,
    request: { name?: string; width: number },
  ): Promise<ApiResponse<RollSizeSpecDto>> {
    const response = await httpClient.put<ApiResponse<RollSizeSpecDto>>(
      NEW_API_ENDPOINTS.companyRollSizeSpecController.PUT(companyId, rollSizeSpecId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "롤 사이즈 규격을 수정하지 못했습니다.");
    }
    return response.data;
  }

  async deleteRollSizeSpec(companyId: number, rollSizeSpecId: number): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyRollSizeSpecController.DELETE(companyId, rollSizeSpecId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "롤 사이즈 규격을 삭제하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Materials ==========

  async getMaterials(companyId: number, type?: string): Promise<ApiResponse<MaterialDto[]>> {
    const response = await httpClient.get<ApiResponse<MaterialDto[]>>(
      NEW_API_ENDPOINTS.companyMaterialsController.GET(companyId, type),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "자재 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async getMaterial(companyId: number, materialId: number): Promise<ApiResponse<MaterialDto>> {
    const response = await httpClient.get<ApiResponse<MaterialDto>>(
      NEW_API_ENDPOINTS.companyMaterialsController.GET_DETAIL(companyId, materialId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "자재 상세 정보를 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createMaterial(
    companyId: number,
    request: { name: string; type: string; paperSpecId?: number; grammage?: number },
  ): Promise<ApiResponse<MaterialDto>> {
    const response = await httpClient.post<ApiResponse<MaterialDto>>(
      NEW_API_ENDPOINTS.companyMaterialsController.POST(companyId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "자재를 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateMaterial(
    companyId: number,
    materialId: number,
    request: { name: string; paperSpecId?: number },
  ): Promise<ApiResponse<MaterialDto>> {
    const response = await httpClient.put<ApiResponse<MaterialDto>>(
      NEW_API_ENDPOINTS.companyMaterialsController.PUT(companyId, materialId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "자재를 수정하지 못했습니다.");
    }
    return response.data;
  }

  async deleteMaterial(companyId: number, materialId: number): Promise<ApiResponse<boolean>> {
    try {
      const response = await httpClient.delete<any>(
        NEW_API_ENDPOINTS.companyMaterialsController.DELETE(companyId, materialId),
      );
      if (response.data.errorCode) {
        const error = new Error(response.data.errorMsg || "자재를 삭제하지 못했습니다.") as any;
        error.errorCode = response.data.errorCode;
        error.products = response.data.data?.products;
        throw error;
      }
      return response.data;
    } catch (err: any) {
      // axios 에러인 경우 (409 등)
      if (err.response?.data) {
        const error = new Error(err.response.data.errorMsg || err.message) as any;
        error.errorCode = err.response.data.errorCode || err.response.status;
        error.products = err.response.data.data?.products;
        throw error;
      }
      // 이미 우리가 만든 에러인 경우 그대로 전달
      if (err.errorCode !== undefined || err.products !== undefined) {
        throw err;
      }
      throw err;
    }
  }

  // ========== Sheet Papers ==========

  async getSheetPapers(
    companyId: number,
    materialId: number,
  ): Promise<ApiResponse<MaterialSheetPaperDto[]>> {
    const response = await httpClient.get<ApiResponse<MaterialSheetPaperDto[]>>(
      NEW_API_ENDPOINTS.companyMaterialSheetPaperController.GET(companyId, materialId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "시트 용지 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createSheetPaper(
    companyId: number,
    materialId: number,
    request: Partial<MaterialSheetPaperDto>,
  ): Promise<ApiResponse<MaterialSheetPaperDto>> {
    const response = await httpClient.post<ApiResponse<MaterialSheetPaperDto>>(
      NEW_API_ENDPOINTS.companyMaterialSheetPaperController.POST(companyId, materialId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "시트 용지를 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateSheetPaper(
    companyId: number,
    materialId: number,
    sheetPaperId: number,
    request: Partial<MaterialSheetPaperDto>,
  ): Promise<ApiResponse<MaterialSheetPaperDto>> {
    const response = await httpClient.put<ApiResponse<MaterialSheetPaperDto>>(
      NEW_API_ENDPOINTS.companyMaterialSheetPaperController.PUT(companyId, materialId, sheetPaperId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "시트 용지를 수정하지 못했습니다.");
    }
    return response.data;
  }

  async deleteSheetPaper(
    companyId: number,
    materialId: number,
    sheetPaperId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyMaterialSheetPaperController.DELETE(companyId, materialId, sheetPaperId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "시트 용지를 삭제하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Roll Papers ==========

  async getRollPapers(
    companyId: number,
    materialId: number,
  ): Promise<ApiResponse<MaterialRollPaperDto[]>> {
    const response = await httpClient.get<ApiResponse<MaterialRollPaperDto[]>>(
      NEW_API_ENDPOINTS.companyMaterialRollPaperController.GET(companyId, materialId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "롤 용지 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createRollPaper(
    companyId: number,
    materialId: number,
    request: Partial<MaterialRollPaperDto>,
  ): Promise<ApiResponse<MaterialRollPaperDto>> {
    const response = await httpClient.post<ApiResponse<MaterialRollPaperDto>>(
      NEW_API_ENDPOINTS.companyMaterialRollPaperController.POST(companyId, materialId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "롤 용지를 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateRollPaper(
    companyId: number,
    materialId: number,
    rollPaperId: number,
    request: Partial<MaterialRollPaperDto>,
  ): Promise<ApiResponse<MaterialRollPaperDto>> {
    const response = await httpClient.put<ApiResponse<MaterialRollPaperDto>>(
      NEW_API_ENDPOINTS.companyMaterialRollPaperController.PUT(companyId, materialId, rollPaperId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "롤 용지를 수정하지 못했습니다.");
    }
    return response.data;
  }

  async deleteRollPaper(
    companyId: number,
    materialId: number,
    rollPaperId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyMaterialRollPaperController.DELETE(companyId, materialId, rollPaperId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "롤 용지를 삭제하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Inks ==========

  async getInks(
    companyId: number,
    materialId: number,
  ): Promise<ApiResponse<InkDto[]>> {
    const response = await httpClient.get<ApiResponse<InkDto[]>>(
      NEW_API_ENDPOINTS.companyMaterialInkController.GET(companyId, materialId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "잉크 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createInk(
    companyId: number,
    materialId: number,
    request: Partial<InkDto>,
  ): Promise<ApiResponse<InkDto>> {
    const response = await httpClient.post<ApiResponse<InkDto>>(
      NEW_API_ENDPOINTS.companyMaterialInkController.POST(companyId, materialId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "잉크를 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateInk(
    companyId: number,
    materialId: number,
    inkId: number,
    request: Partial<InkDto>,
  ): Promise<ApiResponse<InkDto>> {
    const response = await httpClient.put<ApiResponse<InkDto>>(
      NEW_API_ENDPOINTS.companyMaterialInkController.PUT(companyId, materialId, inkId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "잉크를 수정하지 못했습니다.");
    }
    return response.data;
  }

  async deleteInk(
    companyId: number,
    materialId: number,
    inkId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyMaterialInkController.DELETE(companyId, materialId, inkId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "잉크를 삭제하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Ink Configs ==========

  async getInkConfigs(
    companyId: number,
    materialId: number,
  ): Promise<ApiResponse<InkConfigDto[]>> {
    const response = await httpClient.get<ApiResponse<InkConfigDto[]>>(
      NEW_API_ENDPOINTS.companyMaterialInkConfigController.GET(companyId, materialId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "잉크 설정 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createInkConfig(
    companyId: number,
    materialId: number,
    request: Partial<InkConfigDto>,
  ): Promise<ApiResponse<InkConfigDto>> {
    const response = await httpClient.post<ApiResponse<InkConfigDto>>(
      NEW_API_ENDPOINTS.companyMaterialInkConfigController.POST(companyId, materialId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "잉크 설정을 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateInkConfig(
    companyId: number,
    materialId: number,
    inkConfigId: number,
    request: Partial<InkConfigDto>,
  ): Promise<ApiResponse<InkConfigDto>> {
    const response = await httpClient.put<ApiResponse<InkConfigDto>>(
      NEW_API_ENDPOINTS.companyMaterialInkConfigController.PUT(companyId, materialId, inkConfigId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "잉크 설정을 수정하지 못했습니다.");
    }
    return response.data;
  }

  async deleteInkConfig(
    companyId: number,
    materialId: number,
    inkConfigId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyMaterialInkConfigController.DELETE(companyId, materialId, inkConfigId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "잉크 설정을 삭제하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Others ==========

  async getOtherMaterials(
    companyId: number,
    materialId: number,
  ): Promise<ApiResponse<OtherMaterialDto[]>> {
    const response = await httpClient.get<ApiResponse<OtherMaterialDto[]>>(
      NEW_API_ENDPOINTS.companyMaterialOtherController.GET(companyId, materialId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "기타 자재 목록을 가져오지 못했습니다.");
    }
    return response.data;
  }

  async createOtherMaterial(
    companyId: number,
    materialId: number,
    request: Partial<OtherMaterialDto>,
  ): Promise<ApiResponse<OtherMaterialDto>> {
    const response = await httpClient.post<ApiResponse<OtherMaterialDto>>(
      NEW_API_ENDPOINTS.companyMaterialOtherController.POST(companyId, materialId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "기타 자재를 생성하지 못했습니다.");
    }
    return response.data;
  }

  async updateOtherMaterial(
    companyId: number,
    materialId: number,
    otherId: number,
    request: Partial<OtherMaterialDto>,
  ): Promise<ApiResponse<OtherMaterialDto>> {
    const response = await httpClient.put<ApiResponse<OtherMaterialDto>>(
      NEW_API_ENDPOINTS.companyMaterialOtherController.PUT(companyId, materialId, otherId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "기타 자재를 수정하지 못했습니다.");
    }
    return response.data;
  }

  async deleteOtherMaterial(
    companyId: number,
    materialId: number,
    otherId: number,
  ): Promise<ApiResponse<boolean>> {
    const response = await httpClient.delete<ApiResponse<boolean>>(
      NEW_API_ENDPOINTS.companyMaterialOtherController.DELETE(companyId, materialId, otherId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "기타 자재를 삭제하지 못했습니다.");
    }
    return response.data;
  }

  // ========== Inventory ==========

  async getAllInventory(
    companyId: number,
    type?: string,
  ): Promise<ApiResponse<MaterialInventoryDto[]>> {
    const response = await httpClient.get<ApiResponse<MaterialInventoryDto[]>>(
      NEW_API_ENDPOINTS.companyInventoryController.GET_ALL(companyId, type),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "재고 목록을 불러오지 못했습니다.");
    }
    return response.data;
  }

  async getInventory(
    companyId: number,
    materialId: number,
  ): Promise<ApiResponse<MaterialInventoryDto>> {
    const response = await httpClient.get<ApiResponse<MaterialInventoryDto>>(
      NEW_API_ENDPOINTS.companyInventoryController.GET(companyId, materialId),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "재고 정보를 불러오지 못했습니다.");
    }
    return response.data;
  }

  async receiveInventory(
    companyId: number,
    materialId: number,
    request: InventoryRequest,
  ): Promise<ApiResponse<MaterialInventoryDto>> {
    const response = await httpClient.post<ApiResponse<MaterialInventoryDto>>(
      NEW_API_ENDPOINTS.companyInventoryController.POST_RECEIVE(companyId, materialId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "입고 처리에 실패했습니다.");
    }
    return response.data;
  }

  async deductInventory(
    companyId: number,
    materialId: number,
    request: InventoryRequest,
  ): Promise<ApiResponse<MaterialInventoryDto>> {
    const response = await httpClient.post<ApiResponse<MaterialInventoryDto>>(
      NEW_API_ENDPOINTS.companyInventoryController.POST_DEDUCT(companyId, materialId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "차감 처리에 실패했습니다.");
    }
    return response.data;
  }

  async returnInventory(
    companyId: number,
    materialId: number,
    request: InventoryRequest,
  ): Promise<ApiResponse<MaterialInventoryDto>> {
    const response = await httpClient.post<ApiResponse<MaterialInventoryDto>>(
      NEW_API_ENDPOINTS.companyInventoryController.POST_RETURN(companyId, materialId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "반품 처리에 실패했습니다.");
    }
    return response.data;
  }

  async getInventoryLogs(
    companyId: number,
    materialId: number,
    page = 0,
    size = 20,
  ): Promise<ApiResponse<InventoryLogPageResponse>> {
    const response = await httpClient.get<ApiResponse<InventoryLogPageResponse>>(
      NEW_API_ENDPOINTS.companyInventoryController.GET_LOGS(companyId, materialId, page, size),
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "재고 이력을 불러오지 못했습니다.");
    }
    return response.data;
  }

  async updateInventoryLog(
    companyId: number,
    materialId: number,
    logId: number,
    request: InventoryRequest,
  ): Promise<ApiResponse<MaterialInventoryLogDto>> {
    const response = await httpClient.put<ApiResponse<MaterialInventoryLogDto>>(
      NEW_API_ENDPOINTS.companyInventoryController.PUT_LOG(companyId, materialId, logId),
      request,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "이력 수정에 실패했습니다.");
    }
    return response.data;
  }
}

export const companyProductSystemApiService = new CompanyProductSystemApiService();
